import React from 'react'

import { LoyaltyCardStamp } from '@src/components/SingleOutlet/types'
import { LoyaltyCard } from '@src/graphql-types'
import { UseBasketTotalsResponseData } from '@src/hooks/useTotals/useBasketTotals'
import { useBasketTotalsContextOptional } from '@src/hooks/useTotals/BasketTotalsContext'

import {
  Stamp,
  StampsContainer,
  StampProgressMeter,
  StampAndProgressMeterContainer,
  StampText,
  StampTotalText,
  StampBackground,
  AmountEarnedOnStampContainer,
} from './CustomerLoyaltyCard.styles'
import { ScallopedMaskSVG } from '../SVGS/ScallopedMaskSVG'
import { imageJitURL } from '@src/utils/imageJitURL'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { useBreakpoint } from '@src/hooks/useBreakpoint'

export const LoyaltyCardStampProgressMeter: React.FC<{
  loyaltyCard:
    | (Pick<
        LoyaltyCard,
        'requiredStamps' | 'loyaltyCardColor' | 'netTotal' | 'loyaltyCardIcon'
      > & {
        loyaltyCardStamps?: LoyaltyCardStamp[]
        stampsEarnedUpToThisOrder?: number
      })
    | undefined
  isOutletList?: boolean
  canStampBeEarned?: boolean
  basketTotals?: UseBasketTotalsResponseData
  isTermsPage?: boolean
  isBasket?: boolean
  isSingleOrder?: boolean
}> = ({
  loyaltyCard,
  isOutletList = false,
  isTermsPage = false,
  isBasket = false,
  isSingleOrder = false,
  canStampBeEarned = true,
  basketTotals,
}) => {
  const formatCurrency = useFormatCurrency()
  const { isMobile, isSmallerThanWideDesktop, isWideDesktop } = useBreakpoint()

  const contextBasketTotals = useBasketTotalsContextOptional()
  const effectiveBasketTotals =
    basketTotals || contextBasketTotals?.basketTotals.data

  if (!loyaltyCard) return null

  const {
    requiredStamps,
    loyaltyCardStamps,
    loyaltyCardColor,
    netTotal,
    loyaltyCardIcon,
    stampsEarnedUpToThisOrder,
  } = loyaltyCard

  const stampsEarned =
    !isSingleOrder && loyaltyCardStamps
      ? loyaltyCardStamps?.length
      : stampsEarnedUpToThisOrder || 0

  const singleOrderSlider =
    isSingleOrder &&
    ((requiredStamps > 4 && isSmallerThanWideDesktop && !isMobile) ||
      (requiredStamps > 5 && (isMobile || isWideDesktop)))

  const shouldShowSlider =
    !isTermsPage &&
    ((requiredStamps > 5 && isOutletList) ||
      (isMobile && requiredStamps > 5) ||
      singleOrderSlider ||
      requiredStamps >= 7)

  const stampColor = loyaltyCardColor || '#00956a'

  const innerStampScale = isTermsPage ? 0.324 : isOutletList ? 0.187 : 0.225
  const stampBackgroundScale = isTermsPage
    ? 0.362
    : isOutletList
    ? 0.206
    : 0.244

  return (
    <StampsContainer
      isTermsPage={isTermsPage}
      isOutletList={isOutletList}
      requiredStamps={requiredStamps}
      isBasket={isBasket}
      isSingleOrder={isSingleOrder}
      data-test-id="loyalty-card-stampprogress-container-stamps"
    >
      {Array.from({ length: requiredStamps }).map((_, index) => {
        const stampExists = stampsEarned > index
        const isLastEarnedStamp = index === stampsEarned - 1

        const isStartStamp = index === 0
        const isEndStamp = index === requiredStamps - 1
        const hasSingleStamp = stampsEarned <= 1
        const hasMultipleStamps = stampsEarned !== 1
        const shouldShowText =
          !stampExists ||
          (isStartStamp && hasSingleStamp && shouldShowSlider) ||
          (isLastEarnedStamp && hasMultipleStamps && shouldShowSlider)

        const showAnimation =
          canStampBeEarned && effectiveBasketTotals && isBasket

        const shouldShowNextStampToBeEarnedOnSlider =
          (!isStartStamp && showAnimation && index === stampsEarned) || false

        const imageUrl = imageJitURL(loyaltyCardIcon, {
          resize: { width: 120, height: 120, fit: 'cover' },
        })

        return (
          <StampAndProgressMeterContainer
            key={`stamp-${index}`}
            canStampBeEarned={effectiveBasketTotals ? canStampBeEarned : true}
            isEndStamp={isEndStamp}
            isTermsPage={isTermsPage}
            index={index}
            requiredStamps={requiredStamps}
          >
            <div>
              <StampBackground
                showAnimation={
                  (showAnimation && loyaltyCardStamps?.length === index) ||
                  false
                }
                shouldShowAsSlider={shouldShowSlider}
                isStartStamp={isStartStamp}
                isEndStamp={isEndStamp}
                isLastEarnedStamp={isLastEarnedStamp}
                loyaltyCardColor={stampColor}
                hasStampBeenUsed={stampExists}
                key={`stamp-bg-${index}`}
                scale={stampBackgroundScale}
                isTermsPage={isTermsPage}
                numberOfStamps={stampsEarned}
                shouldShowNextStamp={shouldShowNextStampToBeEarnedOnSlider}
                isOutletList={isOutletList}
              >
                <ScallopedMaskSVG scale={stampBackgroundScale} />
              </StampBackground>

              <Stamp
                shouldShowAsSlider={shouldShowSlider}
                isStartStamp={isStartStamp}
                isEndStamp={isEndStamp}
                isLastEarnedStamp={isLastEarnedStamp}
                isOutletList={isOutletList}
                loyaltyCardColor={stampColor}
                hasStampBeenUsed={stampExists}
                key={`stamp-${index}`}
                imageUrl={imageUrl}
                scale={innerStampScale}
                isTermsPage={isTermsPage}
                numberOfStamps={stampsEarned}
                shouldShowNextStamp={shouldShowNextStampToBeEarnedOnSlider}
                data-test-id={`loyalty-card-stampprogress-stamp-${index + 1}`}
              >
                <ScallopedMaskSVG scale={innerStampScale} />
                {shouldShowText && (
                  <StampText
                    hasStampBeenUsed={stampExists}
                    isTermsPage={isTermsPage}
                    showAnimation={
                      (showAnimation && stampsEarned === index) || false
                    }
                    loyaltyCardColor={stampColor}
                  >
                    {index + 1}
                  </StampText>
                )}
              </Stamp>

              {isTermsPage && (
                <AmountEarnedOnStampContainer>
                  <StampTotalText
                    loyaltyCardColor={loyaltyCardColor}
                    isTermsPage={isTermsPage}
                    isDisabledText={
                      !loyaltyCard?.loyaltyCardStamps?.[index]
                        ?.amountEarnedOnOrder
                    }
                    data-test-id={`loyalty-card-stampprogress-text-amountearned-${
                      index + 1
                    }`}
                  >
                    {formatCurrency(
                      loyaltyCard?.loyaltyCardStamps?.[index]
                        ?.amountEarnedOnOrder ?? 0
                    )}
                  </StampTotalText>
                </AmountEarnedOnStampContainer>
              )}
            </div>

            {index + 1 < requiredStamps && (
              <>
                <StampProgressMeter
                  requiredStamps={requiredStamps}
                  loyaltyCardColor={stampColor}
                  hasStampBeenUsed={stampsEarned - 1 > index}
                  isOutletList={isOutletList}
                  isMobile={isMobile}
                  isTermsPage={isTermsPage}
                  index={index}
                  showAnimation={false}
                  includeExtraSpacing={
                    shouldShowSlider &&
                    isBasket &&
                    (isLastEarnedStamp || index == 0)
                  }
                  isBackground={false}
                  isBasket={isBasket}
                >
                  <StampProgressMeter
                    isBackground
                    requiredStamps={requiredStamps}
                    loyaltyCardColor={stampColor}
                    hasStampBeenUsed={stampsEarned - 1 > index}
                    isOutletList={isOutletList}
                    isMobile={isMobile}
                    isTermsPage={isTermsPage}
                    includeExtraSpacing={
                      shouldShowSlider &&
                      isBasket &&
                      (isLastEarnedStamp || index == 0)
                    }
                    isBasket={isBasket}
                    index={index}
                    showAnimation={
                      (showAnimation && stampsEarned === index + 1) || false
                    }
                  />
                </StampProgressMeter>
              </>
            )}
          </StampAndProgressMeterContainer>
        )
      })}
      {isOutletList ||
      (stampsEarned === 0 && !isOutletList && !isTermsPage && !isBasket) ? (
        <StampTotalText
          isDisabledText={stampsEarned === 0 && !isOutletList}
          loyaltyCardColor={loyaltyCardColor}
          isTermsPage={isTermsPage}
          data-test-id="loyalty-card-stampprogress-text-nettotal"
        >
          {formatCurrency(netTotal ?? 0)}
        </StampTotalText>
      ) : null}
    </StampsContainer>
  )
}
